/**
 * Label Filter Context Hooks
 *
 * Hooks for consuming the label filter context.
 */

import { useContext } from 'react'
import { LabelFilterContext } from './LabelFilterContextDef'
import type { LabelFilterContextValue } from '../types'

/**
 * Hook to access the label filter context.
 * Throws an error if used outside of LabelFilterProvider.
 *
 * @example
 * ```tsx
 * const { selectedFilterLabels, setSelectedFilterLabels } = useLabelFilterContext()
 * ```
 */
export function useLabelFilterContext(): LabelFilterContextValue {
  const context = useContext(LabelFilterContext)
  if (context === null) {
    throw new Error('useLabelFilterContext must be used within a LabelFilterProvider')
  }
  return context
}

/**
 * Hook to optionally access the label filter context.
 * Returns null if used outside of LabelFilterProvider.
 * Useful for components that may or may not be within the provider.
 *
 * @example
 * ```tsx
 * const labelFilter = useOptionalLabelFilterContext()
 * const selectedLabels = labelFilter?.selectedFilterLabels ?? []
 * ```
 */
export function useOptionalLabelFilterContext(): LabelFilterContextValue | null {
  return useContext(LabelFilterContext)
}
